import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import API, { fmt } from '../utils/api';
import ProductPicker from '../components/ProductPicker';

const EMPTY = { category_id:'', name:'', brand:'', size:'', unit:'pcs', opening_qty:0, min_stock:0, price:0, supplier:'' };

export default function Products() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [fCat, setFCat] = useState('');
  const [fBrands, setFBrands] = useState(new Set());
  const [fSizes, setFSizes] = useState(new Set());
  const [brandOpen, setBrandOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [fLowStock, setFLowStock] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [importModal, setImportModal] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set()); // IDs المحددة
  const [bulkField, setBulkField] = useState('brand');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const fileRef = useRef();
  const brandRef = useRef();
  const sizeRef = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (brandRef.current && !brandRef.current.contains(e.target)) setBrandOpen(false);
      if (sizeRef.current && !sizeRef.current.contains(e.target)) setSizeOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const isManager = ['admin','sales_manager','warehouse'].includes(user?.role);
  const canImport = ['admin','warehouse'].includes(user?.role);
  const isAdmin = ['admin'].includes(user?.role);

  const load = () => {
    API.get('/products').then(r=>setProducts(r.data));
    API.get('/product-categories').then(r=>setCategories(r.data));
  };
  useEffect(()=>{ load(); },[]);

  const allBrands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort();
  const allSizes  = [...new Set(products.map(p => p.size).filter(Boolean))].sort();

  const filtered = products.filter(p =>
    (!search || (p.name+' '+(p.brand||'')+' '+(p.size||'')).toLowerCase().includes(search.toLowerCase())) &&
    (!fCat        || p.category_id === +fCat) &&
    (!fBrands.size || fBrands.has(p.brand)) &&
    (!fSizes.size  || fSizes.has(p.size)) &&
    (!fLowStock    || p.low_stock)
  );

  const toggleSelect = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(p => p.id)));
  };

  const bulkUpdate = async () => {
    if (!selected.size || !bulkValue.trim()) return;
    if (!window.confirm(`تحديث "${bulkField === 'brand' ? 'الماركة' : bulkField === 'size' ? 'الحجم' : 'التصنيف'}" لـ ${selected.size} منتج إلى "${bulkValue}"؟`)) return;
    setBulkLoading(true);
    try {
      await API.put('/products/bulk-update', { ids: [...selected], field: bulkField, value: bulkValue });
      setSelected(new Set());
      setBulkValue('');
      load();
    } catch(e) { alert(e.response?.data?.error || 'حدث خطأ'); }
    finally { setBulkLoading(false); }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${p.name}" نهائياً؟\nهذا الإجراء لا يمكن التراجع عنه.`)) return;
    try {
      await API.delete(`/products/${p.id}`);
      load();
    } catch(e) { alert(e.response?.data?.error || 'حدث خطأ أثناء الحذف'); }
  };

  const printInventory = () => {
    const win = window.open('', '_blank', 'width=900,height=700');
    const today = new Date().toLocaleDateString('ar-SA');
    const rows = filtered.map((p, i) => `
      <tr class="${i%2===0?'row-even':'row-odd'}">
        <td style="text-align:center;font-weight:800;color:#6d28d9;width:36px">${i+1}</td>
        <td style="color:#475569;font-size:12px">${p.category_name||'-'}</td>
        <td><span class="brand-badge">${p.brand||'-'}</span></td>
        <td style="text-align:center;color:#475569;font-size:12px">${p.size||'-'}</td>
        <td style="font-weight:700;font-size:13px">${p.name}</td>
        <td style="text-align:center;font-weight:800;font-size:13px;color:${p.low_stock?'#dc2626':'#16a34a'}">${p.current_qty} <span style="font-size:11px;font-weight:400">${p.unit}</span></td>
        <td style="text-align:center;color:#94a3b8;font-size:12px">${p.min_stock}</td>
        <td style="text-align:center">
          <span class="${p.low_stock?'badge-low':'badge-ok'}">
            ${p.low_stock?'⚠️ منخفض':'✅ متوفر'}
          </span>
        </td>
      </tr>`).join('');
    const lowCount = filtered.filter(p=>p.low_stock).length;
    win.document.write(`<!DOCTYPE html><html lang="ar" dir="rtl"><head><meta charset="utf-8"/>
      <title>جرد المواد الأولية</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:Tahoma,Arial,sans-serif;direction:rtl;padding:24px 20px;font-size:13px;color:#1e293b}
        .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;padding-bottom:12px;border-bottom:2px solid #6d28d9}
        .header h2{font-size:20px;color:#6d28d9;margin-bottom:4px}
        .header .meta{font-size:12px;color:#64748b}
        .stats{display:flex;gap:16px;margin-bottom:16px}
        .stat{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:8px 16px;text-align:center}
        .stat .num{font-size:18px;font-weight:800;color:#1e293b}
        .stat .lbl{font-size:11px;color:#64748b}
        table{width:100%;border-collapse:collapse}
        thead tr{background:#1e293b}
        th{color:white;padding:9px 10px;text-align:right;font-size:12px;font-weight:700}
        td{padding:8px 10px;border-bottom:1px solid #e2e8f0;vertical-align:middle}
        .row-even td{background:#ffffff}
        .row-odd td{background:#eef2ff}
        .brand-badge{background:#e0e7ff;color:#3730a3;border-radius:4px;padding:1px 7px;font-weight:700;font-size:12px}
        .badge-low{background:#fee2e2;color:#dc2626;border-radius:20px;padding:2px 10px;font-size:11px;font-weight:700}
        .badge-ok{background:#dcfce7;color:#16a34a;border-radius:20px;padding:2px 10px;font-size:11px;font-weight:700}
        @media print{
          @page{margin:1.2cm;size:A4 portrait}
          body{padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          .no-print{display:none}
          .row-even td{background:#ffffff !important}
          .row-odd td{background:#eef2ff !important}
          .brand-badge{background:#e0e7ff !important;color:#3730a3 !important}
          .badge-low{background:#fee2e2 !important;color:#dc2626 !important}
          .badge-ok{background:#dcfce7 !important;color:#16a34a !important}
        }
      </style></head><body>
      <div class="header">
        <div>
          <h2>🏷️ جرد المواد الأولية</h2>
          <div class="meta">التاريخ: ${today}</div>
        </div>
        <div class="stats">
          <div class="stat"><div class="num">${filtered.length}</div><div class="lbl">إجمالي المواد</div></div>
          ${lowCount>0?`<div class="stat" style="border-color:#fca5a5"><div class="num" style="color:#dc2626">${lowCount}</div><div class="lbl" style="color:#dc2626">مخزون منخفض</div></div>`:''}
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th style="width:36px;text-align:center">#</th>
            <th>التصنيف</th>
            <th>البراند</th>
            <th style="text-align:center">الحجم</th>
            <th>المنتج</th>
            <th style="text-align:center">الكمية</th>
            <th style="text-align:center">الحد الأدنى</th>
            <th style="text-align:center">الحالة</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="no-print" style="margin-top:20px;text-align:center">
        <button onclick="window.print()" style="padding:10px 28px;background:#6d28d9;color:white;border:none;border-radius:8px;font-size:14px;cursor:pointer">🖨️ طباعة</button>
      </div>
      </body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 500);
  };

  const save = async () => {
    if (!form.name) return setError('اسم المنتج مطلوب');
    try {
      if (editing) await API.put(`/products/${editing}`, form);
      else await API.post('/products', form);
      setModal(false); load();
    } catch(e) { setError(e.response?.data?.error||'حدث خطأ'); }
  };

  // Excel Import
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        // Parse Excel manually using basic CSV detection or ask user to verify
        const text = evt.target.result;
        // Try to use SheetJS if available
        if (window.XLSX) {
          const wb = window.XLSX.read(evt.target.result, { type: 'binary' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const data = window.XLSX.utils.sheet_to_json(ws);
          setImportData(data.slice(0, 200));
        } else {
          alert('مكتبة قراءة ملفات Excel لسا عم تحمّل، انتظري ثانيتين وحاولي ترفعي الملف مرة تانية');
          e.target.value = '';
        }
      } catch(e) { alert('تعذر قراءة الملف'); }
    };
    reader.readAsBinaryString(file);
  };

  const submitImport = async () => {
    if (!importData.length) return;
    setImporting(true);
    try {
      const items = importData.map(row => ({
        name: row['اسم المنتج'] || row['Item Name'] || row['name'] || '',
        brand: row['البراند'] || row['Brand'] || row['brand'] || '',
        size: row['الحجم'] || row['Size'] || row['size'] || '',
        unit: row['الوحدة'] || row['Unit'] || row['unit'] || 'pcs',
        current_qty: +(row['الكمية الحالية'] || row['Current Qty'] || row['current_qty'] || 0),
        min_stock: +(row['الحد الأدنى'] || row['Min Stock'] || row['min_stock'] || 0),
        supplier: row['المورد'] || row['Supplier'] || row['supplier'] || '',
        price: +(row['السعر'] || row['Price'] || row['price'] || 0),
      })).filter(i => i.name);
      const res = await API.post('/products/import', { items });
      setImportResult(res.data);
      load();
    } catch(e) { alert(e.response?.data?.error||'حدث خطأ'); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">🏷️ المنتجات</h1>
        <div className="page-actions">
          {canImport && <button className="btn btn-secondary" onClick={()=>{setImportModal(true);setImportResult(null);setImportData([]);}}>📥 استيراد Excel</button>}
          {isManager && <button className="btn btn-primary" onClick={()=>{setEditing(null);setForm(EMPTY);setError('');setModal(true);}}>+ منتج جديد</button>}
        </div>
      </div>

      <div className="search-bar" style={{ flexWrap:'wrap', gap:8 }}>
        <input className="search-input" placeholder="🔍 بحث بالاسم..." value={search} onChange={e=>setSearch(e.target.value)} style={{ minWidth:180 }} />
        <select value={fCat} onChange={e=>setFCat(e.target.value)} style={{minWidth:150}}>
          <option value="">كل التصنيفات</option>
          {categories.map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
        </select>
        {/* Brands Multi-Select */}
        <div ref={brandRef} style={{ position:'relative' }}>
          <button className="btn btn-secondary btn-sm" onClick={()=>{setBrandOpen(!brandOpen);setSizeOpen(false);}}
            style={{ minWidth:130, display:'flex', justifyContent:'space-between', alignItems:'center', gap:6 }}>
            <span>{fBrands.size ? `${fBrands.size} ماركة` : 'كل الماركات'}</span>
            <span style={{fontSize:'0.7rem'}}>▼</span>
          </button>
          {brandOpen && (
            <div style={{ position:'absolute', top:'110%', right:0, background:'white', border:'1px solid var(--border)', borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', zIndex:999, minWidth:160, padding:8 }}>
              {allBrands.map(b => {
                const checked = fBrands.has(b);
                return (
                  <div key={b} onClick={()=>{ const n=new Set(fBrands); checked?n.delete(b):n.add(b); setFBrands(n); }}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', cursor:'pointer', borderRadius:6, fontSize:'0.85rem', fontWeight:checked?700:400, background:checked?'#ede9fe':'transparent', color:checked?'#6d28d9':'#374151', transition:'all 0.12s', userSelect:'none' }}>
                    <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${checked?'#6d28d9':'#d1d5db'}`, background:checked?'#6d28d9':'white', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.12s' }}>
                      {checked && <span style={{ color:'white', fontSize:12, fontWeight:900, lineHeight:1 }}>✓</span>}
                    </div>
                    {b}
                  </div>
                );
              })}
              {fBrands.size > 0 && <button className="btn btn-sm btn-secondary" style={{width:'100%',marginTop:4}} onClick={()=>setFBrands(new Set())}>مسح</button>}
            </div>
          )}
        </div>

        {/* Sizes Multi-Select */}
        <div ref={sizeRef} style={{ position:'relative' }}>
          <button className="btn btn-secondary btn-sm" onClick={()=>{setSizeOpen(!sizeOpen);setBrandOpen(false);}}
            style={{ minWidth:120, display:'flex', justifyContent:'space-between', alignItems:'center', gap:6 }}>
            <span>{fSizes.size ? `${fSizes.size} حجم` : 'كل الأحجام'}</span>
            <span style={{fontSize:'0.7rem'}}>▼</span>
          </button>
          {sizeOpen && (
            <div style={{ position:'absolute', top:'110%', right:0, background:'white', border:'1px solid var(--border)', borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', zIndex:999, minWidth:150, padding:8, maxHeight:240, overflowY:'auto' }}>
              {allSizes.map(s => {
                const checked = fSizes.has(s);
                return (
                  <div key={s} onClick={()=>{ const n=new Set(fSizes); checked?n.delete(s):n.add(s); setFSizes(n); }}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', cursor:'pointer', borderRadius:6, fontSize:'0.85rem', fontWeight:checked?700:400, background:checked?'#ede9fe':'transparent', color:checked?'#6d28d9':'#374151', transition:'all 0.12s', userSelect:'none' }}>
                    <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${checked?'#6d28d9':'#d1d5db'}`, background:checked?'#6d28d9':'white', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.12s' }}>
                      {checked && <span style={{ color:'white', fontSize:12, fontWeight:900, lineHeight:1 }}>✓</span>}
                    </div>
                    {s}
                  </div>
                );
              })}
              {fSizes.size > 0 && <button className="btn btn-sm btn-secondary" style={{width:'100%',marginTop:4}} onClick={()=>setFSizes(new Set())}>مسح</button>}
            </div>
          )}
        </div>
        <button className={`btn btn-sm ${fLowStock?'btn-danger':'btn-secondary'}`} onClick={()=>setFLowStock(!fLowStock)}>
          ⚠️ المخزون المنخفض
        </button>
        {(search||fCat||fBrands.size||fSizes.size||fLowStock) && (
          <button className="btn btn-secondary btn-sm" onClick={()=>{setSearch('');setFCat('');setFBrands(new Set());setFSizes(new Set());setFLowStock(false);}}>✕ مسح الفلاتر</button>
        )}
        <span className="text-muted" style={{fontSize:'0.8rem'}}>{filtered.length} منتج</span>
        <button className="btn btn-secondary btn-sm" onClick={printInventory} style={{marginRight:'auto'}}>🖨️ طباعة الجرد</button>
      </div>

      {/* Import Modal */}
      {importModal && (
        <div className="modal-overlay" onClick={()=>setImportModal(false)}>
          <div className="modal" style={{maxWidth:700}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">📥 استيراد المنتجات من Excel</span>
              <button className="modal-close" onClick={()=>setImportModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="alert alert-info">
                الأعمدة المدعومة: <strong>اسم المنتج / Item Name</strong>، البراند / Brand، الحجم / Size، الوحدة / Unit، الكمية الحالية / Current Qty، الحد الأدنى / Min Stock، المورد / Supplier، السعر / Price
              </div>
              <input type="file" accept=".xlsx,.xls,.csv" ref={fileRef} onChange={handleFileChange} style={{marginBottom:12}} />
              {/* Load XLSX dynamically */}
              <script src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"></script>
              {importData.length > 0 && (
                <>
                  <p className="text-muted mb-1">تم تحميل <strong>{importData.length}</strong> صف. معاينة أول 5:</p>
                  <div className="table-scroll" style={{maxHeight:200,overflow:'auto'}}>
                    <table><thead><tr>{Object.keys(importData[0]).slice(0,6).map(k=><th key={k}>{k}</th>)}</tr></thead>
                    <tbody>{importData.slice(0,5).map((r,i)=><tr key={i}>{Object.values(r).slice(0,6).map((v,j)=><td key={j}>{String(v||'')}</td>)}</tr>)}</tbody></table>
                  </div>
                </>
              )}
              {importResult && <div className="alert alert-success mt-1">✅ {importResult.message} (تم: {importResult.added}، تخطي: {importResult.skipped})</div>}
            </div>
            <div className="modal-footer">
              {importData.length > 0 && !importResult && (
                <button className="btn btn-primary" onClick={submitImport} disabled={importing}>{importing?'جاري الاستيراد...':'📥 استيراد الآن'}</button>
              )}
              <button className="btn btn-secondary" onClick={()=>setImportModal(false)}>إغلاق</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={()=>setModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editing?'تعديل منتج':'منتج جديد'}</span>
              <button className="modal-close" onClick={()=>setModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <div className="form-grid">
                <div className="form-group span-2"><label>الاسم <span className="required">*</span></label><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></div>
                <div className="form-group"><label>التصنيف</label>
                  <select value={form.category_id} onChange={e=>setForm({...form,category_id:e.target.value})}>
                    <option value="">-- اختر --</option>
                    {categories.map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>البراند</label><input value={form.brand||''} onChange={e=>setForm({...form,brand:e.target.value})} /></div>
                <div className="form-group"><label>الحجم</label><input value={form.size||''} onChange={e=>setForm({...form,size:e.target.value})} /></div>
                <div className="form-group"><label>الوحدة</label>
                  <select value={form.unit||'pcs'} onChange={e=>setForm({...form,unit:e.target.value})}>
                    {['pcs','kg','g','L','ml','box','carton','dozen'].map(u=><option key={u}>{u}</option>)}
                  </select>
                </div>
                {!editing && <div className="form-group"><label>الكمية الافتتاحية</label><input type="number" value={form.opening_qty||0} onChange={e=>setForm({...form,opening_qty:+e.target.value})} min={0} /></div>}
                <div className="form-group"><label>الحد الأدنى للمخزون</label><input type="number" value={form.min_stock||0} onChange={e=>setForm({...form,min_stock:+e.target.value})} min={0} /></div>
                <div className="form-group"><label>سعر البيع</label><input type="number" value={form.price||0} onChange={e=>setForm({...form,price:+e.target.value})} min={0} /></div>
                <div className="form-group span-2"><label>المورد</label><input value={form.supplier||''} onChange={e=>setForm({...form,supplier:e.target.value})} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={save}>💾 حفظ</button>
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Bar */}
      {selected.size > 0 && (
        <div style={{ background:'#4c1d95', borderRadius:10, padding:'10px 16px', marginBottom:12, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <span style={{ color:'white', fontWeight:700, fontSize:'0.9rem' }}>✅ تم تحديد {selected.size} منتج</span>
          <select value={bulkField} onChange={e=>{setBulkField(e.target.value);setBulkValue('');}}
            style={{ padding:'5px 10px', borderRadius:6, border:'none', fontSize:'0.85rem', fontWeight:600 }}>
            <option value="brand">تعديل الماركة</option>
            <option value="size">تعديل الحجم</option>
            <option value="unit">تعديل الوحدة</option>
            <option value="supplier">تعديل المورد</option>
          </select>
          {bulkField === 'unit' ? (
            <select value={bulkValue} onChange={e=>setBulkValue(e.target.value)}
              style={{ padding:'5px 10px', borderRadius:6, border:'none', fontSize:'0.85rem', minWidth:100 }}>
              <option value="">-- اختر --</option>
              {['pcs','kg','g','L','ml','box','carton','dozen'].map(u=><option key={u}>{u}</option>)}
            </select>
          ) : (
            <input value={bulkValue} onChange={e=>setBulkValue(e.target.value)}
              placeholder={`القيمة الجديدة...`}
              style={{ padding:'5px 10px', borderRadius:6, border:'none', fontSize:'0.85rem', minWidth:140 }} />
          )}
          <button className="btn btn-sm" onClick={bulkUpdate} disabled={bulkLoading||!bulkValue.trim()}
            style={{ background:'#10b981', color:'white', border:'none' }}>
            {bulkLoading ? '...' : '✔ تطبيق'}
          </button>
          <button className="btn btn-sm" onClick={()=>{setSelected(new Set());setBulkValue('');}}
            style={{ background:'rgba(255,255,255,0.2)', color:'white', border:'none' }}>
            إلغاء
          </button>
        </div>
      )}

      <div className="table-container">
        <div className="table-scroll">
          <table>
            <thead><tr>
              {isAdmin && <th style={{width:36,textAlign:'center'}}>
                <div onClick={toggleAll} style={{width:18,height:18,borderRadius:4,border:`2px solid ${selected.size===filtered.length&&filtered.length>0?'#6d28d9':'#d1d5db'}`,background:selected.size===filtered.length&&filtered.length>0?'#6d28d9':'white',display:'inline-flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
                  {selected.size===filtered.length&&filtered.length>0&&<span style={{color:'white',fontSize:11,fontWeight:900}}>✓</span>}
                </div>
              </th>}
              <th>المنتج</th><th>التصنيف</th><th>البراند</th><th>الحجم</th><th>الكمية</th><th>الحد الأدنى</th><th>السعر</th><th>الحالة</th>{isManager&&<th>تعديل</th>}{isAdmin&&<th>حذف</th>}
            </tr></thead>
            <tbody>
              {filtered.map(p=>(
                <tr key={p.id} style={{background: selected.has(p.id)?'#ede9fe':undefined}}>
                  {isAdmin && <td style={{textAlign:'center'}}>
                    <div onClick={()=>toggleSelect(p.id)} style={{width:18,height:18,borderRadius:4,border:`2px solid ${selected.has(p.id)?'#6d28d9':'#d1d5db'}`,background:selected.has(p.id)?'#6d28d9':'white',display:'inline-flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
                      {selected.has(p.id)&&<span style={{color:'white',fontSize:11,fontWeight:900}}>✓</span>}
                    </div>
                  </td>}
                  <td className="font-bold">{p.name}</td>
                  <td><span className="badge badge-info">{p.category_icon} {p.category_name}</span></td>
                  <td className="text-muted">{p.brand||'-'}</td>
                  <td className="text-muted">{p.size||'-'}</td>
                  <td><span className={`badge ${p.low_stock?'badge-danger':'badge-success'}`}>{p.current_qty} {p.unit}</span></td>
                  <td className="text-muted">{p.min_stock}</td>
                  <td className="text-success">{fmt.currency(p.price)}</td>
                  <td><span className={`badge ${p.low_stock?'badge-danger':'badge-success'}`}>{p.low_stock?'⚠️ منخفض':'✅ متوفر'}</span></td>
                  {isManager&&<td><button className="btn btn-sm btn-secondary" onClick={()=>{setEditing(p.id);setForm({...p});setError('');setModal(true);}}>تعديل</button></td>}
                  {isAdmin&&<td><button className="btn btn-sm btn-danger" onClick={()=>deleteProduct(p)}>🗑️ حذف</button></td>}
                </tr>
              ))}
              {filtered.length===0&&<tr><td colSpan={isAdmin?11:9}><div className="empty-state"><div className="empty-state-icon">🏷️</div><p>لا توجد منتجات</p></div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
